import React from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import TrendingDownRounded from '@mui/icons-material/TrendingDownRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import SellRounded from '@mui/icons-material/SellRounded';
import FilterAltRounded from '@mui/icons-material/FilterAltRounded';
import ClearRounded from '@mui/icons-material/ClearRounded';
import { useFeature } from '../../ui/FeaturesProvider';
import { usePageBanner } from '../../ui/PageBannerProvider';
import { fetchDialOpSnapshot } from '../../services/dialingOpportunityService';
import { formatAbsolute, formatRelativeTime, parseBackendDate } from '../../utils/date';
import type {
  DialOpCallType,
  DialOpLeadPool,
  DialOpSnapshot,
} from '../../types/dialingOpportunity';
import SettingsPageHeader from '../settings/components/SettingsPageHeader';
import DialOpPeriodContext from './components/DialOpPeriodContext';
import DialOpportunityLoadingSkeleton from './components/DialOpportunityLoadingSkeleton';
import ContactWindowHeatmap from './components/ContactWindowHeatmap';
import DemandShiftTable from './components/DemandShiftTable';
import CohortGrid from './components/CohortGrid';
import ReportCardTable from './components/ReportCardTable';
import AgentViewTable from './components/AgentViewTable';
import StateFilterSelect from './components/StateFilterSelect';
import AgentFilterSelect from './components/AgentFilterSelect';
import TaskTypeFilterSelect from './components/TaskTypeFilterSelect';
import CollapsibleSection from './components/CollapsibleSection';
import PeriodComparePicker, {
  type CustomRange,
  defaultDialOpRange,
  isDialOpCompareEnabled,
  resolveDialOpComparePeriod,
  snapshotMatchesRange,
} from './components/PeriodComparePicker';
import StateBreakdownTable from './components/StateBreakdownTable';
import SegmentToggle from './components/SegmentToggle';
import DataBadge, { type DataBadgeKind } from './components/DataBadge';
import LeadRosterSection from './components/build2/LeadRosterSection';

function DeltaBadge({
  deltaPct,
  higherIsBad,
}: {
  deltaPct?: number | null;
  higherIsBad?: boolean;
}) {
  const theme = useTheme();
  if (deltaPct === null || deltaPct === undefined) return null;
  const up = deltaPct > 0;
  const flat = deltaPct === 0;
  const bad = higherIsBad ? up : !up && !flat;
  const color = flat
    ? theme.palette.text.secondary
    : bad
      ? theme.palette.error.main
      : theme.palette.success.main;
  const arrow = flat ? '→' : up ? '↑' : '↓';
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color, fontWeight: 700 }}>
      <span>{arrow}</span>
      <span>{Math.abs(deltaPct).toFixed(1)}%</span>
    </Box>
  );
}

/** Primary headline stat — large, and clickable to drill into the leads behind it. */
function HeadlineStat({
  label,
  value,
  accent,
  deltaPct,
  higherIsBad,
  compareValue,
  tooltip,
  children,
  onClick,
  badge,
  placeholder,
  placeholderNote,
}: {
  label: string;
  value: string;
  accent?: 'warning' | 'error' | 'default';
  deltaPct?: number | null;
  higherIsBad?: boolean;
  compareValue?: string;
  tooltip?: string;
  children?: React.ReactNode;
  onClick?: () => void;
  badge?: DataBadgeKind;
  /** Renders a dash instead of a value — for metrics we don't have data for yet. */
  placeholder?: boolean;
  placeholderNote?: string;
}) {
  const theme = useTheme();
  const color =
    accent === 'error'
      ? theme.palette.error.main
      : accent === 'warning'
        ? theme.palette.warning.main
        : theme.palette.text.primary;
  const showDelta = deltaPct !== undefined;

  return (
    <Paper
      elevation={0}
      onClick={placeholder ? undefined : onClick}
      sx={(t) => ({
        p: 2.5,
        height: '100%',
        borderRadius: `${t.ds.radius.md}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.raised,
        cursor: onClick && !placeholder ? 'pointer' : 'default',
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        ...(onClick && !placeholder
          ? {
              '&:hover': {
                borderColor: alpha(t.palette.primary.main, 0.5),
                bgcolor: alpha(t.palette.primary.main, 0.03),
              },
            }
          : {}),
      })}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          {label}
        </Typography>
        {badge ? <DataBadge kind={badge} /> : null}
        {tooltip ? (
          <Tooltip title={tooltip} arrow placement="top">
            <InfoOutlined sx={{ fontSize: 13, color: 'text.disabled' }} />
          </Tooltip>
        ) : null}
      </Stack>
      {placeholder ? (
        <>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.main, mt: 0.75 }}>
            –
          </Typography>
          {placeholderNote ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.5 }}>
              {placeholderNote}
            </Typography>
          ) : null}
        </>
      ) : (
        <>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.75 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color }}>
              {value}
            </Typography>
            {showDelta ? (
              <Typography variant="body2">
                <DeltaBadge deltaPct={deltaPct} higherIsBad={higherIsBad} />
              </Typography>
            ) : null}
          </Stack>
          {showDelta && compareValue ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              vs {compareValue} in the period before
            </Typography>
          ) : null}
          {children ? <Box sx={{ mt: 0.75 }}>{children}</Box> : null}
          {onClick ? (
            <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mt: 1.25 }}>
              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                View these leads
              </Typography>
              <ArrowForwardRounded sx={{ fontSize: 13, color: 'primary.main' }} />
            </Stack>
          ) : null}
        </>
      )}
    </Paper>
  );
}

/** Compact context card for the non-anchor populations (appointments, sold pool). */
function PopulationCard({
  icon,
  title,
  blurb,
  stats,
  onDrill,
  drillLabel,
  badge,
}: {
  icon: React.ReactNode;
  title: string;
  blurb: string;
  stats: { value: string; label: string }[];
  onDrill: () => void;
  drillLabel: string;
  badge?: DataBadgeKind;
}) {
  return (
    <Paper
      elevation={0}
      sx={(t) => ({
        p: 2.5,
        height: '100%',
        borderRadius: `${t.ds.radius.md}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
      })}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
        {icon}
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        {badge ? <DataBadge kind={badge} /> : null}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {blurb}
      </Typography>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        {stats.map((s) => (
          <Box key={s.label}>
            <Typography variant="h5" fontWeight={700}>
              {s.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {s.label}
            </Typography>
          </Box>
        ))}
      </Stack>
      <Button
        size="small"
        onClick={onDrill}
        endIcon={<ArrowForwardRounded sx={{ fontSize: 15 }} />}
        sx={{ mt: 2, textTransform: 'none', fontWeight: 600, px: 0 }}
      >
        {drillLabel}
      </Button>
    </Paper>
  );
}

/** Main section heading with a hover explanation of what the section covers. */
function SectionHeading({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
        {label}
      </Typography>
      <Tooltip title={tooltip} arrow placement="top">
        <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
      </Tooltip>
    </Stack>
  );
}

export default function DialingOpportunityPage() {
  const feature = useFeature('dialing_opportunity_monitor');
  const [custom, setCustom] = React.useState<CustomRange | null>(() => defaultDialOpRange());
  const [snapshot, setSnapshot] = React.useState<DialOpSnapshot | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // "Applied" filters drive the actual snapshot fetch below. "Draft" filters
  // are what the toolbar controls are bound to -- they only flow into
  // "applied" (and trigger a re-fetch) when the user clicks Apply, so picking
  // several states/agents doesn't fire a warehouse query per click.
  const [stateFilter, setStateFilter] = React.useState<string[]>([]);
  const [agentFilter, setAgentFilter] = React.useState<string[]>([]);
  const [draftStateFilter, setDraftStateFilter] = React.useState<string[]>([]);
  const [draftAgentFilter, setDraftAgentFilter] = React.useState<string[]>([]);
  // UI preview only -- Regal call-task types aren't in the warehouse yet, so
  // this doesn't filter anything. See TaskTypeFilterSelect.
  const [taskTypeFilter, setTaskTypeFilter] = React.useState<string[]>([]);
  const [callType, setCallType] = React.useState<DialOpCallType>('first_call_connect');
  const [leadPool, setLeadPool] = React.useState<DialOpLeadPool>('fresh');
  const [activeTab, setActiveTab] = React.useState<'build1' | 'build2'>('build1');
  const snapshotRef = React.useRef<DialOpSnapshot | null>(null);
  const drillRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  const loadSnapshot = React.useCallback(
    async (
      range: CustomRange | null,
      forceRefresh = false,
      state: string[] = stateFilter,
      agent: string[] = agentFilter,
    ) => {
      if (!feature.enabled) return;
      const hadSnapshot = snapshotRef.current != null;
      setLoading(true);
      if (hadSnapshot) setRefreshing(true);
      setError(null);
      try {
        const data = await fetchDialOpSnapshot(
          range
            ? {
                currentStart: range.currentStart,
                currentEnd: range.currentEnd,
                compareStart: range.compareStart,
                compareEnd: range.compareEnd,
                state,
                agent,
                refresh: forceRefresh || undefined,
              }
            : { state, agent, refresh: forceRefresh || undefined },
        );
        setSnapshot(data);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load monitor');
        if (!hadSnapshot) setSnapshot(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [feature.enabled, stateFilter, agentFilter],
  );

  React.useEffect(() => {
    void loadSnapshot(custom, false, stateFilter, agentFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custom, stateFilter, agentFilter, loadSnapshot]);

  const compareEnabled = isDialOpCompareEnabled(custom);
  const snapshotMatchesQuery = Boolean(snapshot && custom && snapshotMatchesRange(snapshot, custom));
  const isInitialLoad = loading && !snapshot;
  const isUpdating = loading && !!snapshot && feature.enabled;
  const isApplyingNewView = isUpdating && !snapshotMatchesQuery;
  const showPageSkeleton = isInitialLoad;

  usePageBanner(
    isUpdating
      ? {
          message: isApplyingNewView
            ? 'Updating data for your selected period…'
            : 'Refreshing data from the warehouse…',
          tone: 'info',
        }
      : null,
  );

  // The filter dropdowns' option lists need the *full* universe of states/
  // agents, not just what's left after the current filter narrows the
  // snapshot's own state_breakdown/agent_view down. So: only ever grow these
  // from an unfiltered-on-that-axis snapshot (never shrink them just because
  // a filter is currently narrowing what came back), so a user can still
  // switch to (or add) a state/agent they haven't selected yet.
  const [knownStates, setKnownStates] = React.useState<string[]>([]);
  const [knownAgents, setKnownAgents] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (!snapshot) return;
    // state_breakdown and agent_view are each scoped by *both* filters (see
    // service.build_snapshot), so either one being active can narrow either
    // list -- only trust them as "the full universe" when both are clear.
    if (stateFilter.length === 0 && agentFilter.length === 0) {
      const states = (snapshot.state_breakdown ?? [])
        .map((s) => s.state_cd)
        .filter((s): s is string => !!s)
        .sort();
      setKnownStates(states);
      const agents = (snapshot.agent_view ?? [])
        .map((a) => a.agent_full_nm)
        .filter((a): a is string => !!a)
        .sort();
      setKnownAgents(agents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot]);

  const toggleDraftStateFilter = React.useCallback((state: string) => {
    setDraftStateFilter((prev) =>
      prev.includes(state) ? prev.filter((s) => s !== state) : [...prev, state],
    );
  }, []);

  const toggleDraftAgentFilter = React.useCallback((agent: string) => {
    setDraftAgentFilter((prev) =>
      prev.includes(agent) ? prev.filter((a) => a !== agent) : [...prev, agent],
    );
  }, []);

  const sameFilterSet = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const sa = [...a].sort();
    const sb = [...b].sort();
    return sa.every((v, i) => v === sb[i]);
  };

  const filtersDirty =
    !sameFilterSet(draftStateFilter, stateFilter) || !sameFilterSet(draftAgentFilter, agentFilter);
  const hasActiveFilters =
    stateFilter.length > 0 || agentFilter.length > 0 || draftStateFilter.length > 0 || draftAgentFilter.length > 0;

  const applyFilters = React.useCallback(() => {
    setStateFilter(draftStateFilter);
    setAgentFilter(draftAgentFilter);
  }, [draftStateFilter, draftAgentFilter]);

  const clearFilters = React.useCallback(() => {
    setDraftStateFilter([]);
    setDraftAgentFilter([]);
    setStateFilter([]);
    setAgentFilter([]);
  }, []);

  const focusDrill = React.useCallback(() => {
    window.requestAnimationFrame(() => {
      drillRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const drillInto = React.useCallback(
    (ct: DialOpCallType, lp: DialOpLeadPool) => {
      setCallType(ct);
      setLeadPool(lp);
      focusDrill();
    },
    [focusDrill],
  );

  if (!feature.enabled) {
    return (
      <Box sx={{ maxWidth: 720 }}>
        <Alert severity="info">
          Dialing Opportunity Monitor is not enabled for this workspace. Enable it under Settings → Features.
        </Alert>
      </Box>
    );
  }

  const pendingCompare = custom ? resolveDialOpComparePeriod(custom) : null;
  const displayCurrentPeriod =
    isApplyingNewView && custom
      ? { start: custom.currentStart, end: custom.currentEnd }
      : snapshot?.current_period;
  const displayPriorPeriod =
    isApplyingNewView && custom
      ? pendingCompare
        ? { start: pendingCompare.start, end: pendingCompare.end }
        : { start: custom.currentStart, end: custom.currentEnd }
      : snapshot?.prior_period;

  const updateButtonLabel = refreshing ? 'Updating…' : 'Re-run';

  const computedAt = snapshot ? parseBackendDate(snapshot.computed_at) : null;
  const computedRelative = computedAt ? formatRelativeTime(computedAt) : null;
  const updatedTooltip = computedAt
    ? `Calculated ${formatAbsolute(computedAt)} · Call facts come from the Regal telephony system; warehouse data lags one day.`
    : 'Calculation time unavailable';
  const updatedCaption = [
    computedRelative ? `Updated ${computedRelative}` : null,
    snapshot?.from_cache ? 'cached' : null,
  ].filter(Boolean).join(' · ');

  return (
    <Box sx={{ maxWidth: 1680, mx: 'auto', width: '100%' }}>
      <SettingsPageHeader description="Of the leads we bought this period, how many got called at the best time — and what did the gap cost in conversations, sales, and dollars?" />

      {/* Toolbar — period, filters and refresh. Dialing performance only: the
          roster on Smart scheduling is a forward-looking list anchored on the
          newest data in the warehouse, so a period selector would imply control
          it doesn't have. */}
      {activeTab === 'build1' ? (
      <Paper
        variant="outlined"
        sx={(t) => ({
          px: { xs: 1.5, sm: 2 },
          py: 1,
          mb: 1.5,
          borderRadius: `${t.ds.radius.md}px`,
        })}
      >
        <Stack spacing={1}>
          {/* Row 1: period + last-updated status, always on their own line so
              the filter rows below never fight it for space. */}
          <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap gap={1}>
            <PeriodComparePicker
              value={custom}
              activeStart={snapshot?.current_period?.start ?? null}
              activeEnd={snapshot?.current_period?.end ?? null}
              onApply={(range) => setCustom(range)}
              disabled={loading}
            />
            <Box sx={{ ml: { xs: 0, sm: 'auto' } }}>
              {snapshot ? (
                <Tooltip title={updatedTooltip} arrow>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ cursor: 'default' }}>
                    {updatedCaption}
                  </Typography>
                </Tooltip>
              ) : null}
            </Box>
          </Stack>

          {/* Row 2: the filter controls, grouped tightly with fixed
              widths (chips overflow to "+N" instead of growing the
              toolbar) so this row wraps cleanly regardless of how many
              states/agents are selected. */}
          <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap gap={1} sx={{ pt: 0.5, borderTop: 1, borderColor: 'divider' }}>
            <StateFilterSelect
              options={knownStates}
              value={draftStateFilter}
              onChange={setDraftStateFilter}
              disabled={loading}
            />
            <AgentFilterSelect
              options={knownAgents}
              value={draftAgentFilter}
              onChange={setDraftAgentFilter}
              disabled={loading}
            />
            <Tooltip
              title="Preview only — task types from the Regal call system. Not wired to any data yet, so selecting a type won't change what's shown below."
              arrow
              placement="top"
            >
              <span>
                <TaskTypeFilterSelect
                  value={taskTypeFilter}
                  onChange={setTaskTypeFilter}
                  disabled={loading}
                />
              </span>
            </Tooltip>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: { xs: 0, sm: 0.5 } }}>
              <Tooltip
                title={
                  filtersDirty
                    ? 'Apply the state/agent filter changes below to the whole page.'
                    : 'No pending filter changes.'
                }
                arrow
              >
                <span>
                  <Badge color="warning" variant="dot" invisible={!filtersDirty}>
                    <Button
                      size="small"
                      variant={filtersDirty ? 'contained' : 'outlined'}
                      color={filtersDirty ? 'primary' : 'inherit'}
                      startIcon={<FilterAltRounded sx={{ fontSize: 16 }} />}
                      onClick={applyFilters}
                      disabled={loading || !filtersDirty}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Apply filters
                    </Button>
                  </Badge>
                </span>
              </Tooltip>
              {hasActiveFilters ? (
                <Tooltip title="Clear the state and agent filters." arrow>
                  <span>
                    <Button
                      size="small"
                      color="inherit"
                      startIcon={<ClearRounded sx={{ fontSize: 16 }} />}
                      onClick={clearFilters}
                      disabled={loading}
                      sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
                    >
                      Clear
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>

          {/* Row 3: view options + refresh -- unaffected by how wide the
              filter chips above get. */}
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            gap={1}
            sx={{ pt: 0.5, borderTop: 1, borderColor: 'divider' }}
          >
            <Tooltip
              title="Scopes the lead-level detail and drill-throughs below. The headline numbers above stay fresh-pool by design — sold leads are worked by many buyers and aren't comparable to the calling-quality benchmark."
              arrow
              placement="top"
            >
              <span>
                <SegmentToggle<DialOpLeadPool>
                  label="Lead pool"
                  value={leadPool}
                  onChange={setLeadPool}
                  disabled={loading}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'fresh', label: 'Fresh' },
                    { value: 'sold', label: 'Sold' },
                  ]}
                />
              </span>
            </Tooltip>
            <Tooltip
              title={
                refreshing
                  ? 'Loading updated data from the warehouse'
                  : snapshot?.from_cache
                    ? 'Recalculate from warehouse (bypasses cache)'
                    : 'Refresh data for this period'
              }
              arrow
            >
              <span>
                <Button
                  size="small"
                  variant={snapshot?.from_cache && !refreshing ? 'contained' : 'outlined'}
                  startIcon={
                    refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshRounded />
                  }
                  onClick={() => void loadSnapshot(custom, true)}
                  disabled={loading}
                  sx={{ ml: { xs: 0, sm: 'auto' } }}
                >
                  {updateButtonLabel}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>
      ) : null}

      <Tabs
        value={activeTab}
        onChange={(_e, next) => setActiveTab(next)}
        sx={{ mb: 2, minHeight: 36, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          value="build1"
          label="Dialing performance"
          sx={{ minHeight: 36, textTransform: 'none', fontWeight: 600 }}
        />
        <Tab
          value="build2"
          label={
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box
                component="span"
                sx={{
                  px: 0.75,
                  borderRadius: 0.75,
                  fontSize: '0.625rem',
                  fontWeight: 800,
                  lineHeight: 1.7,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#5C4405',
                  background: 'linear-gradient(135deg, #FFE9A8 0%, #F0C24B 100%)',
                  border: '1px solid #D9A81F',
                  boxShadow: '0 1px 2px rgba(180, 140, 20, 0.25)',
                }}
              >
                New
              </Box>
              <span>Smart scheduling</span>
            </Stack>
          }
          sx={{ minHeight: 36, textTransform: 'none', fontWeight: 600 }}
        />
      </Tabs>

      {activeTab === 'build2' ? (
        <Box sx={{ mb: 3 }}>
          <LeadRosterSection />
        </Box>
      ) : null}

      {activeTab === 'build1' ? (
        <>
      <DialOpPeriodContext
        loading={showPageSkeleton}
        currentPeriod={displayCurrentPeriod}
        priorPeriod={displayPriorPeriod}
        compareEnabled={compareEnabled}
        methodologyNote={isUpdating ? null : snapshot?.methodology_note}
      />

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      {showPageSkeleton ? (
        <DialOpportunityLoadingSkeleton />
      ) : snapshot && !snapshot.data_available && !isUpdating ? (
        <Alert severity="warning">{snapshot.message ?? 'No data available for this period.'}</Alert>
      ) : snapshot ? (
        <Box
          sx={{
            opacity: isUpdating ? 0.55 : 1,
            pointerEvents: isUpdating ? 'none' : 'auto',
            transition: 'opacity 0.2s ease',
          }}
        >
          <Stack spacing={3}>
            {/* ---- Band 1: the gap (fixed anchor — fresh leads, first calls) ---- */}
            <Stack spacing={1.5}>
              <SectionHeading
                label="What the gap cost · fresh leads, first calls"
                tooltip="Fresh, first-time-called leads only. Compares what you actually reached this period against each state's proven best-calling-hour performance, then converts that gap into estimated lost conversations, sales, and commission."
              />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <HeadlineStat
                    label="Conversations missed"
                    value={snapshot.total_missed_connects.toFixed(1)}
                    accent="warning"
                    deltaPct={compareEnabled ? snapshot.comparison?.delta_pct?.total_missed_connects : undefined}
                    compareValue={compareEnabled ? snapshot.comparison?.total_missed_connects.toFixed(1) : undefined}
                    higherIsBad
                    tooltip="Conversations lost by calling outside a state's best hours, plus conversations forfeited entirely because a lead was never called."
                    onClick={() => drillInto('first_call_connect', 'fresh')}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {snapshot.missed_connects.toFixed(1)} mistimed
                      {' · '}
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 700,
                          color: snapshot.never_contacted_count > 0 ? 'error.main' : 'text.secondary',
                        }}
                      >
                        {snapshot.never_contacted_count.toLocaleString()} never called
                      </Box>
                      {' '}({snapshot.never_contacted_missed_connects.toFixed(1)} lost)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      out of {snapshot.mql_population.toLocaleString()} qualified leads
                      {snapshot.unscored_count > 0
                        ? ` (${snapshot.unscored_count.toLocaleString()} not yet scored, included as unscored)`
                        : ''}
                    </Typography>
                  </HeadlineStat>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <HeadlineStat
                    label="Sales missed (CAS)"
                    value={snapshot.estimated_cas_lost.toFixed(1)}
                    accent="error"
                    deltaPct={compareEnabled ? snapshot.comparison?.delta_pct?.estimated_cas_lost : undefined}
                    compareValue={compareEnabled ? snapshot.comparison?.estimated_cas_lost.toFixed(1) : undefined}
                    higherIsBad
                    tooltip="Missed conversations converted to applications using the workspace's own measured contact-to-CAS rate."
                    onClick={() => drillInto('first_call_connect', 'fresh')}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {snapshot.connect_to_cas_pct.toFixed(0)}% of conversations become a sale
                    </Typography>
                  </HeadlineStat>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <HeadlineStat
                    label="Dollars missed"
                    value={`$${Math.round(snapshot.estimated_commission_lost).toLocaleString()}`}
                    deltaPct={compareEnabled ? snapshot.comparison?.delta_pct?.estimated_commission_lost : undefined}
                    compareValue={
                      compareEnabled && snapshot.comparison
                        ? `$${Math.round(snapshot.comparison.estimated_commission_lost).toLocaleString()}`
                        : undefined
                    }
                    higherIsBad
                    tooltip="From real carrier commission data — total_commission_earned, not a guessed percentage."
                    onClick={() => drillInto('first_call_connect', 'fresh')}
                  >
                    <Typography variant="body2" color="text.secondary">
                      commission · ${Math.round(snapshot.estimated_premium_lost).toLocaleString()} premium
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {snapshot.dollar_basis.is_measured ? 'measured' : 'estimated'} per-CAS basis (gross)
                    </Typography>
                  </HeadlineStat>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <HeadlineStat
                    label="Gross margin missed"
                    value=""
                    badge="placeholder"
                    placeholder
                    placeholderNote={
                      snapshot.gross_margin_note ??
                      'Commission missed, minus lead cost, minus agent cost. We have lead cost. Agent cost is missing — needed from the customer.'
                    }
                  />
                </Grid>
              </Grid>

              <Paper
                elevation={0}
                sx={(t) => ({
                  p: 2.5,
                  borderRadius: `${t.ds.radius.md}px`,
                  border: `1px solid ${t.tokens.border.subtle}`,
                  bgcolor: alpha(t.palette.primary.main, 0.04),
                })}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
                  <TrendingDownRounded fontSize="small" color="action" sx={{ mt: 0.25 }} />
                  <Typography variant="subtitle2" fontWeight={700}>
                    What this means
                  </Typography>
                </Stack>
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  {snapshot.summary}
                </Typography>
              </Paper>
            </Stack>

            {/* ---- Band 2: where the gap is ---- */}
            <Stack spacing={1.5}>
              <SectionHeading
                label="Where the gap is"
                tooltip="A heatmap of historical connect rates by state and hour of day, plus a per-state breakdown of missed conversations — so you can see exactly where dialing effort and answer-likelihood are out of sync."
              />
              {snapshot.contact_window?.states?.length ? (
                <ContactWindowHeatmap
                  window={snapshot.contact_window}
                  baselineWeeks={snapshot.baseline_weeks ?? 12}
                />
              ) : null}
              {snapshot.state_breakdown?.length ? (
                <StateBreakdownTable
                  rows={snapshot.state_breakdown}
                  baselineWeeks={snapshot.baseline_weeks ?? 12}
                  driftRecentWeeks={snapshot.best_window_drift?.recent_weeks ?? 4}
                  selectedStates={draftStateFilter}
                  onStateToggle={toggleDraftStateFilter}
                />
              ) : null}
            </Stack>

            {/* ---- Other populations, tracked separately (never blended into the anchor) ---- */}
            <Stack spacing={1.5}>
              <SectionHeading
                label="Other populations · measured separately"
                tooltip="Scheduled appointments and the shared sold/resale pool are tracked here on their own terms — kept rate and contact rate — since they're structurally different from fresh first-time calls and aren't folded into the headline numbers above."
              />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PopulationCard
                    icon={<EventAvailableRounded fontSize="small" color="action" />}
                    title="Scheduled appointments"
                    blurb="A booked callback is a kept-commitment problem, not a cold-call timing one — so it's scored on kept rate, not best-hour deviation."
                    stats={[
                      {
                        value: snapshot.appointment_segment.appointment_calls.toLocaleString(),
                        label: 'Appointment calls',
                      },
                      { value: `${snapshot.appointment_segment.kept_rate_pct.toFixed(1)}%`, label: 'Kept rate' },
                      {
                        value: snapshot.appointment_segment.cas_from_appointments.toLocaleString(),
                        label: 'Sales (CAS)',
                      },
                    ]}
                    drillLabel="View appointment leads"
                    onDrill={() => drillInto('scheduled_appointment', 'all')}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <PopulationCard
                    icon={<SellRounded fontSize="small" color="action" />}
                    title="Shared sold / resale pool"
                    blurb="Resold leads are worked by many buyers long after their lead date — structurally lower contact rate, deliberately kept out of the fresh benchmark above."
                    stats={[
                      { value: snapshot.sold_pool.dials.toLocaleString(), label: 'Dials' },
                      { value: snapshot.sold_pool.connects.toLocaleString(), label: 'Connects (90s+)' },
                      { value: `${snapshot.sold_pool.connect_pct.toFixed(1)}%`, label: 'Contact rate' },
                    ]}
                    drillLabel="View sold-pool leads"
                    onDrill={() => drillInto('all', 'sold')}
                  />
                </Grid>
              </Grid>
            </Stack>

            {/* ---- Band 3: who and when (the drill-down) ---- */}
            <Stack spacing={1.5} ref={drillRef}>
              <SectionHeading
                label="Who and when"
                tooltip="Lead-level detail behind the headline numbers: a day-by-day view of how fast new leads got their first successful contact, plus a filterable table of every lead in the current view — whether they were dialed, and how that call went."
              />
              {snapshot.cohort?.length ? <CohortGrid rows={snapshot.cohort} /> : null}
              {snapshot.current_period?.start && snapshot.current_period?.end ? (
                <ReportCardTable
                  currentStart={snapshot.current_period.start}
                  currentEnd={snapshot.current_period.end}
                  callType={callType}
                  leadPool={leadPool}
                  stateCd={stateFilter}
                  agent={agentFilter}
                  onCallTypeChange={setCallType}
                />
              ) : null}
            </Stack>

            {/* ---- Secondary breakdowns (collapsed by default to reduce noise) ---- */}
            <CollapsibleSection
              title="By agent"
              subtitle="Per-agent dials, connect rate, best-window share, appointment kept rate, and sales. Descriptive only — not a ranking."
            >
              <AgentViewTable
                rows={snapshot.agent_view}
                selectedAgents={draftAgentFilter}
                onAgentToggle={toggleDraftAgentFilter}
                bare
              />
            </CollapsibleSection>

            {snapshot.demand_shift?.movers?.length ? (
              <CollapsibleSection
                title="Lead geography mix"
                subtitle="Where your leads are coming from this period vs. the prior weeks — incoming demand, not calling quality."
              >
                <DemandShiftTable shift={snapshot.demand_shift} bare selectedStateCount={stateFilter.length} />
              </CollapsibleSection>
            ) : null}
          </Stack>
        </Box>
      ) : null}
        </>
      ) : null}
    </Box>
  );
}
